import { useEffect, useRef, useState } from "react";

import { ClientsService } from "../services/clientsService";
import type { OrganizationSearchItem } from "../types/clients-api.types";

const DEFAULT_DEBOUNCE_MS = 350;
const LIST_LIMIT = 50;

interface UseDebouncedOrganizationSearchResult {
  organizations: OrganizationSearchItem[];
  isLoading: boolean;
  debouncedSearch: string;
}

/**
 * Server-side organization search by name with debounce. Hits GET /internal/clients/organizations/search,
 * which matches over the whole organizations table (partial, case-insensitive) — unlike the tree endpoint,
 * which only returns a page of the most recently created organizations.
 */
export function useDebouncedOrganizationSearch(
  enabled: boolean,
  searchInput: string,
  debounceMs: number = DEFAULT_DEBOUNCE_MS,
): UseDebouncedOrganizationSearchResult {
  const [result, setResult] = useState<UseDebouncedOrganizationSearchResult>({
    organizations: [],
    isLoading: false,
    debouncedSearch: "",
  });
  const requestIdRef = useRef(0);

  useEffect(() => {
    const id = ++requestIdRef.current;
    const trimmed = searchInput.trim();

    const timer = window.setTimeout(() => {
      if (id !== requestIdRef.current) return;

      if (!enabled || !trimmed) {
        setResult({ organizations: [], isLoading: false, debouncedSearch: trimmed });
        return;
      }

      setResult((prev) => ({ ...prev, isLoading: true, debouncedSearch: trimmed }));

      ClientsService.searchOrganizations({
        skip: 0,
        limit: LIST_LIMIT,
        name: trimmed,
      })
        .then((res) => {
          if (id !== requestIdRef.current) return;
          setResult({ organizations: res.data.data ?? [], isLoading: false, debouncedSearch: trimmed });
        })
        .catch(() => {
          if (id !== requestIdRef.current) return;
          setResult({ organizations: [], isLoading: false, debouncedSearch: trimmed });
        });
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [enabled, searchInput, debounceMs]);

  return result;
}
